import { Alert, SlideDown, Switch, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, DurationUnit, formatDate, FormField, isEquipmentBased, isScheduled, now, parseTime } from "common";
import dayjs from "dayjs";
import { omit } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AllowedPrivacy, BaseScheduleType, Experience, Permission, ScheduleType } from "types";
import { useSeller } from "../../../hooks/useAccount";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useResource } from "../../../hooks/useResource";
import { useVisibility } from "../../../hooks/useVisibility";
import { getDefaultGroup } from "../../../models/Experience";
import { isPurchaseItemizationEnabled } from "../../../models/Seller";
import { IsGranted } from "../../Common/IsGranted";
import { TimePickerPopover } from "../../Common/TimePickerPopover";
import { ExperienceDurationField } from "../ExperienceDurationField";
import { SchedulesTable } from "./SchedulesTable";

interface SchedulesProps {
    values: Experience;
    errors?: any;
    onChange: (values: any) => void;
}

export const Schedules = ({ values, errors, onChange }: SchedulesProps) => {
    const { id } = useParams<{ id?: ID; section?: string }>();
    const seller = useSeller();
    const { isGranted } = useAuthorization();
    const [arrivalOffsetToggle, setIsOpenArrivalOffsetToggle] = useState(!!values.arrivalOffset);
    const [alertDisplayed, { show: showAlert, hide: hideAlert }] = useVisibility();
    const canScheduleToggle = values.schedules?.length === 0;
    const [scheduleToggle, setScheduleToggle] = useState(isScheduled(values));
    // Equipment-based pricing requires schedules, so block toggling them off (XS-1742)
    const isScheduleLockedForEquipment = scheduleToggle && isEquipmentBased(values);
    const product = useResource<Experience>(["products", id]).data;
    const virtualMeeting = product?.virtualMeeting?.enabled;

    const initialDuration = useRef(values.eventDuration);
    const alertData = getStartEndDateTime(values);
    const preposition = alertData?.startTime ? "at" : "on";

    useEffect(() => {
        if (!values?.eventDuration) {
            return;
        }

        const isLongerThanOneDay = values.eventDuration > 1440; // minutes
        const isEventDurationChanged = values.eventDuration !== initialDuration.current;

        if (isLongerThanOneDay && isEventDurationChanged) {
            showAlert();
        } else {
            hideAlert();
        }
    }, [values?.eventDuration, showAlert, hideAlert]);

    const handleArrivalOffsetToggle = (value: boolean) => {
        setIsOpenArrivalOffsetToggle(value);

        if (!value) {
            onChange({ ...values, arrivalOffset: null });
            return;
        }

        onChange({ ...values, arrivalOffset: 0 });
    };

    const handleScheduleToggle = (value: boolean) => {
        if (!value && isEquipmentBased(values)) {
            return;
        }

        // If the experience is unscheduled, we will have only one type of privacy
        const allowedPrivacies = value ? [...values.allowedPrivacies] : [AllowedPrivacy.PUBLIC];

        const getGroup = () => {
            // If experience became scheduled, merge defaults with existing group
            if (value) {
                return { ...getDefaultGroup(), ...values.group };
            }

            // If experience became unscheduled, remove outing fields
            if (values.group) {
                return omit(values.group, ["outingMin", "outingMax", "outingMinCutoff"]);
            }

            return { orderMin: 1 };
        };

        setScheduleToggle(value);
        onChange({
            ...values,
            group: getGroup(),
            scheduleType: value ? ScheduleType.DAY : ScheduleType.OPEN,
            allowedPrivacies,
        });
    };

    const handleEventDurationChange = (value: number) => {
        const updatedValues = { ...values, eventDuration: value };
        /**
         * Seller don't have inventory permissions. so updating
         * duration field based on eventDuration.
         */
        if (!isGranted(Permission.MODIFY_RESOURCES_EQUIPMENT)) {
            updatedValues.duration = value;
        }

        onChange(updatedValues);
    };

    return (
        <div className="flex space-x-8">
            <div className="flex w-full min-w-0 flex-col space-y-4">
                {scheduleToggle && product && (
                    <div className="space-y-4">
                        <ExperienceDurationField
                            label="Event Duration"
                            name="eventDuration"
                            originalValue={product?.eventDuration ?? 0}
                            value={values.eventDuration ?? 0}
                            error={errors.eventDuration}
                            onChange={handleEventDurationChange}
                        />

                        <SlideDown isOpen={alertDisplayed && !!alertData}>
                            <Alert className="max-w-[380px]">
                                <p>
                                    E.g. The event starting {preposition}&nbsp;
                                    <var data-var="startDateAndTime">
                                        {alertData?.startTime ? alertData?.startTime : null} {alertData?.startDate}
                                    </var>
                                    &nbsp;will end {preposition}{" "}
                                    <var data-var="endDateAndTime">
                                        {alertData?.endTime ? alertData?.endTime : null} {alertData?.endDate}
                                    </var>
                                </p>
                            </Alert>
                        </SlideDown>

                        <IsGranted permissions={Permission.MODIFY_RESOURCES_EQUIPMENT}>
                            <ExperienceDurationField
                                label="Inventory Duration"
                                name="duration"
                                tooltip="The amount of time an event booked on this product will occupy assigned equipment items"
                                originalValue={product?.duration ?? 0}
                                value={values.duration ?? 0}
                                error={errors.duration}
                                onChange={(value: number) => onChange({ ...values, duration: value })}
                            />
                        </IsGranted>
                    </div>
                )}

                <div>
                    <span className="text-sm font-bold">Schedule</span>
                    <div className="flex items-center space-x-2">
                        <Tooltip
                            content="Schedules cannot be turned off for equipment-based experiences"
                            disabled={!isScheduleLockedForEquipment}
                        >
                            <span>
                                <Switch
                                    data-testid="schedules-schedule-switch"
                                    size="medium"
                                    isChecked={scheduleToggle}
                                    disabled={
                                        !canScheduleToggle ||
                                        isPurchaseItemizationEnabled(seller) ||
                                        isScheduleLockedForEquipment
                                    }
                                    onChange={handleScheduleToggle}
                                />
                            </span>
                        </Tooltip>
                        <span>Guests must choose an arrival date from a predefined list of schedules</span>
                    </div>
                </div>

                {scheduleToggle ? (
                    <div className="space-y-8">
                        <SchedulesTable experience={values} tableScheduleType={BaseScheduleType.AVAILABLE} />

                        <SchedulesTable experience={values} tableScheduleType={BaseScheduleType.UNAVAILABLE} />

                        {virtualMeeting ? null : (
                            <div className="flex flex-col space-y-2">
                                <span className="text-sm font-bold">Early Arrival</span>
                                <div className="flex items-center space-x-2">
                                    <Switch
                                        data-testid="schedules-early-arrival-switch"
                                        size="medium"
                                        isChecked={arrivalOffsetToggle}
                                        onChange={(value: boolean) => handleArrivalOffsetToggle(value)}
                                    />
                                    <Tooltip
                                        content="Arrival Offset must be greater then 0"
                                        disabled={!errors?.arrivalOffset}
                                    >
                                        <span className={clsx(errors?.arrivalOffset ? "!text-danger" : "")}>
                                            Customers should arrive
                                            {arrivalOffsetToggle ? (
                                                <FormField name="arrivalOffset">
                                                    <TimePickerPopover
                                                        showArrow
                                                        dataTestId="early-arrival-time"
                                                        classNames={{
                                                            text: "px-0.5 pb-0.5 mx-1 !text-black !border-black",
                                                        }}
                                                        preferredUnit={DurationUnit.MINUTE}
                                                        value={values.arrivalOffset ?? 0}
                                                        onApply={(value: number) =>
                                                            onChange({ ...values, arrivalOffset: value })
                                                        }
                                                    />
                                                    before scheduled time
                                                </FormField>
                                            ) : (
                                                <span>&nbsp;before scheduled time</span>
                                            )}
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const getStartEndDateTime = (values: Experience) => {
    if (!values?.eventDuration) {
        return null;
    }

    const firstSchedule = values?.schedules.find((schedule) => schedule.type === BaseScheduleType.AVAILABLE);
    const [firstScheduleTimeSlot, firstScheduleDate] = firstSchedule?.start
        ? [firstSchedule.times?.[0], formatDate(firstSchedule.start)]
        : [null, now().toString()];
    const startDateTime = firstScheduleTimeSlot
        ? getDateWithTime(firstScheduleDate, firstScheduleTimeSlot)
        : dayjs(firstScheduleDate).startOf("day");
    // Subtract 1 more minute for events starting at 12am so that the end date shows     up correctly
    const endDateTime = firstScheduleTimeSlot
        ? startDateTime.add(values.eventDuration, "minutes")
        : startDateTime.add(values.eventDuration - 1, "minutes");

    return {
        startTime: firstScheduleTimeSlot && formatDate(startDateTime, DateFormat.TIME).toLowerCase().replace(" ", ""),
        startDate: formatDate(startDateTime, DateFormat.DATE),
        endTime: firstScheduleTimeSlot && formatDate(endDateTime, DateFormat.TIME).toLowerCase().replace(" ", ""),
        endDate: formatDate(endDateTime, DateFormat.DATE),
    };
};

const getDateWithTime = (date: string, time: number) => {
    const { hours, minutes } = parseTime(time);
    return dayjs(date).set("hours", hours).set("minutes", minutes).startOf("minute");
};
